import { createSlice } from "@reduxjs/toolkit";
import {
  getPhotoGalleryWidgetConfigAction,
  getPhotoGalleryWidgetListAction,
  getPhotoGalleryWidgetJsonldAction,
  addPhotoGalleryWidgetAction,
  editPhotoGalleryWidgetAction,
  addPostImageAction,
} from "../actions/PhotoGalleryWidgetActions";

interface PhotoGalleryWidgetState {
  config: {
    data: any;
    loading: boolean;
    error: any;
  };
  list: {
    data: any[];
    nextCursor: string | null;
    loading: boolean;
    error: any;
  };
  jsonld: {
    data: any;
    loading: boolean;
    error: any;
  };
  isSaving: boolean;
  isAdding: boolean;
  isAddingImage: boolean;
}

const initialState: PhotoGalleryWidgetState = {
  config: {
    data: null,
    loading: false,
    error: null,
  },
  list: {
    data: [],
    nextCursor: null,
    loading: false,
    error: null,
  },
  jsonld: {
    data: null,
    loading: false,
    error: null,
  },
  isSaving: false,
  isAdding: false,
  isAddingImage: false,
};

const photoGalleryWidgetSlice = createSlice({
  name: "photoGalleryWidget",
  initialState,
  reducers: {
    resetPhotoGalleryWidgetState: (state) => {
      state.config = initialState.config;
      state.list = initialState.list;
      state.jsonld = initialState.jsonld;
    },
  },
  extraReducers: (builder) => {
    // Config
    builder
      .addCase(getPhotoGalleryWidgetConfigAction.pending, (state) => {
        state.config.loading = true;
        state.config.error = null;
      })
      .addCase(getPhotoGalleryWidgetConfigAction.fulfilled, (state, action) => {
        state.config.loading = false;
        state.config.data = action.payload?.data || action.payload;
      })
      .addCase(getPhotoGalleryWidgetConfigAction.rejected, (state, action) => {
        state.config.loading = false;
        state.config.error = action.payload;
      });

    // List
    builder
      .addCase(getPhotoGalleryWidgetListAction.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(getPhotoGalleryWidgetListAction.fulfilled, (state, action) => {
        state.list.loading = false;
        const incomingData = action.payload?.data || [];
        const isPagination = Boolean(action.meta?.arg?.cursor);
        state.list.data = isPagination
          ? [...state.list.data, ...incomingData]
          : incomingData;
        state.list.nextCursor = action.payload?.nextCursor ?? null;
      })
      .addCase(getPhotoGalleryWidgetListAction.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload;
      });

    // JsonLD
    builder
      .addCase(getPhotoGalleryWidgetJsonldAction.pending, (state) => {
        state.jsonld.loading = true;
        state.jsonld.error = null;
      })
      .addCase(getPhotoGalleryWidgetJsonldAction.fulfilled, (state, action) => {
        state.jsonld.loading = false;
        state.jsonld.data = action.payload?.data || action.payload;
      })
      .addCase(getPhotoGalleryWidgetJsonldAction.rejected, (state, action) => {
        state.jsonld.loading = false;
        state.jsonld.error = action.payload;
      });

    // Add
    builder
      .addCase(addPhotoGalleryWidgetAction.pending, (state) => {
        state.isAdding = true;
      })
      .addCase(addPhotoGalleryWidgetAction.fulfilled, (state) => {
        state.isAdding = false;
      })
      .addCase(addPhotoGalleryWidgetAction.rejected, (state) => {
        state.isAdding = false;
      });

    // Edit
    builder
      .addCase(editPhotoGalleryWidgetAction.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(editPhotoGalleryWidgetAction.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(editPhotoGalleryWidgetAction.rejected, (state) => {
        state.isSaving = false;
      });

    // Add Post Image
    builder
      .addCase(addPostImageAction.pending, (state) => {
        state.isAddingImage = true;
      })
      .addCase(addPostImageAction.fulfilled, (state) => {
        state.isAddingImage = false;
      })
      .addCase(addPostImageAction.rejected, (state) => {
        state.isAddingImage = false;
      });
  },
});

export const { resetPhotoGalleryWidgetState } = photoGalleryWidgetSlice.actions;
export default photoGalleryWidgetSlice.reducer;
